/**
 * A way back (Zac, 2026-10-28): opening a patient, chart or ledger from Ask
 * Velora, or a page Settings links to (Add and retire offices, Insurance
 * plans and carriers), left no way back, and the desktop app has no browser
 * Back button at all.
 *
 * Two pieces:
 *   - `HistoryBackButton`: an arrow in the top bar that steps back through
 *     this tab's in-app history (the browser's Back, which the desktop app and
 *     the installed app do not show). Disabled on the first page of the tab.
 *   - `BackToBar`: "Back to Settings" / "Back to Ask Velora" over the page
 *     after leaving a hub page. A hub page calls `useBackOrigin(label)`; when
 *     it unmounts because the person followed a link out, the bar offers that
 *     exact page (with its section, tab and search) again. It goes away when
 *     the person returns to a hub, dismisses it, or picks a sidebar item.
 */
import { useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import { ArrowLeft, X } from 'lucide-react';
import { useEffect, useRef, useSyncExternalStore } from 'react';

export interface BackTarget {
  href: string;
  label: string;
}

let target: BackTarget | null = null;
/** When the person last picked a sidebar/menu item or pressed Back (ms). */
let freshStartAt = 0;
const FRESH_START_MS = 5000;
const listeners = new Set<() => void>();

function setTarget(next: BackTarget | null): void {
  if (next === target) return;
  target = next;
  for (const l of listeners) l();
}

export const backTrail = {
  get: (): BackTarget | null => target,
  set: setTarget,
  clear: (): void => setTarget(null),
  /** A sidebar pick or history Back: leaving a hub this way offers no bar. */
  freshStart(): void {
    freshStartAt = Date.now();
    setTarget(null);
  },
  /** A hub page was left (its unmount). */
  left(t: BackTarget): void {
    if (Date.now() - freshStartAt < FRESH_START_MS) return;
    setTarget(t);
  },
  subscribe(l: () => void): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

/**
 * Marks the current page as a hub ("Settings", "Ask Velora"). While it is
 * shown there is no bar; when the person leaves it, the bar offers it back.
 */
export function useBackOrigin(label: string): void {
  // The router moves `location` to the NEXT page while this one is still
  // mounted (pending load), so remember the href only while the router is
  // idle: that is the hub page itself, with its section, tab and search.
  const { href, idle } = useRouterState({
    select: (s) => ({ href: s.location.href, idle: s.status === 'idle' }),
    structuralSharing: true,
  });
  const own = useRef<string | null>(null);
  if (idle) own.current = href;
  useEffect(() => {
    backTrail.clear();
    return () => {
      if (own.current) backTrail.left({ href: own.current, label });
    };
  }, [label]);
}

/** Sidebar and classic menu picks are a fresh start: no "Back to" bar. */
function useClearOnNavPick(): void {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const el = e.target instanceof Element ? e.target : null;
      if (el?.closest('.vel-nav, .vel-menubar__menus, .vel-classic__chrome')) backTrail.freshStart();
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);
}

export function BackToBar() {
  const t = useSyncExternalStore(backTrail.subscribe, backTrail.get, backTrail.get);
  const navigate = useNavigate();
  useClearOnNavPick();
  if (!t) return null;
  return (
    <div className="shell-backbar" role="navigation" aria-label="Back">
      <button
        type="button"
        className="shell-backbar__link"
        onClick={() => {
          backTrail.clear();
          void navigate({ href: t.href });
        }}
      >
        <ArrowLeft size={14} aria-hidden />
        <span>Back to {t.label}</span>
      </button>
      <button
        type="button"
        className="shell-backbar__close"
        aria-label="Dismiss"
        title="Dismiss"
        onClick={() => backTrail.clear()}
      >
        <X size={13} aria-hidden />
      </button>
    </div>
  );
}

export function HistoryBackButton() {
  const router = useRouter();
  // Re-render on every navigation so canGoBack is current.
  useRouterState({ select: (s) => s.location.state.__TSR_index });
  const can = router.history.canGoBack();
  return (
    <button
      type="button"
      className="shell-iconbtn shell-histback"
      aria-label="Back"
      title="Back"
      disabled={!can}
      onClick={() => {
        backTrail.freshStart();
        router.history.back();
      }}
    >
      <ArrowLeft size={17} aria-hidden />
    </button>
  );
}
